'use client';

import { X } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useRef, type ReactNode } from 'react';

import { Button } from '@platform/ui/components/button';
import { useDialogFocus } from '@platform/ui/lib/dialog-focus';
import { cn } from '@platform/ui/lib/utils';

/** sm and md hold a decision or a short form; full holds one picture at a time (docs/10). */
export type ModalSize = 'sm' | 'md' | 'full';

const SIZES: Record<ModalSize, string> = {
    sm: 'max-w-120',
    md: 'max-w-160',
    full: 'h-[calc(100dvh-2rem)] max-w-none max-sm:top-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:rounded-none max-sm:border-0',
};

export interface ModalProps {
    open: boolean;
    onClose: () => void;
    title: ReactNode;
    description?: ReactNode;
    children?: ReactNode;
    footer?: ReactNode;
    size?: ModalSize;
    /** The close button's accessible name, in the app's language. */
    closeLabel: string;
    /** Paints the title in the error colour, for a destructive decision. */
    tone?: 'danger';
}

/**
 * One decision, or a short form of one to three fields (docs/11 §7); anything
 * longer is a Drawer. Focus starts in the first field and stays inside, Escape
 * and the scrim close it, focus returns to what opened it, and on a phone it
 * rises from the bottom as a sheet with its primary button first.
 */
export function Modal({ open, onClose, title, description, children, footer, size = 'sm', closeLabel, tone }: ModalProps) {
    const content = useRef<HTMLDivElement>(null);
    const focus = useDialogFocus(content);

    return (
        <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-overlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
                <DialogPrimitive.Content
                    ref={content}
                    // Radix warns about a dialog with no description unless told there is none on purpose.
                    {...(description ? {} : { 'aria-describedby': undefined })}
                    onOpenAutoFocus={focus.onOpenAutoFocus}
                    onCloseAutoFocus={focus.onCloseAutoFocus}
                    className={cn(
                        'fixed z-50 flex w-[calc(100%-2rem)] flex-col rounded-overlay border border-border bg-popover text-popover-foreground shadow-overlay outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0',
                        'top-1/2 left-1/2 max-h-[calc(100dvh-2rem)] -translate-1/2',
                        'max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:max-h-[90dvh] max-sm:w-full max-sm:max-w-none max-sm:translate-0 max-sm:rounded-b-none max-sm:border-b-0',
                        // After the phone's sheet, so the viewer's whole screen wins over it.
                        SIZES[size],
                    )}
                >
                    <header className="flex items-start justify-between gap-4 px-6 pt-5">
                        <div className="min-w-0">
                            <DialogPrimitive.Title
                                className={cn('text-18 font-semibold tracking-heading', tone === 'danger' && 'text-destructive-strong')}
                            >
                                {title}
                            </DialogPrimitive.Title>
                            {description && (
                                <DialogPrimitive.Description className="mt-2 text-13 text-muted-foreground">
                                    {description}
                                </DialogPrimitive.Description>
                            )}
                        </div>
                        <DialogPrimitive.Close asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={closeLabel} className="-mt-1 -mr-2">
                                <X aria-hidden="true" />
                            </Button>
                        </DialogPrimitive.Close>
                    </header>
                    {children && <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-4">{children}</div>}
                    {footer ? (
                        <footer className="flex flex-col-reverse gap-2 px-6 pt-5 pb-6 max-sm:*:w-full sm:flex-row sm:justify-end">{footer}</footer>
                    ) : (
                        <div className="pb-6" />
                    )}
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
